// Financial formatting helpers for the Earnings Calendar table: compact
// billions/millions currency (USD for US, EUR for DE), the FMP reporting-time
// badge mapping, and a timezone-safe day header.

import type { MarketType } from '../api/useEarningsCalendar';

export const formatCurrency = (
    value: number | null,
    market: MarketType,
): string => {
    if (value === null || value === undefined) return '-';

    const isBillion = value >= 1_000_000_000;
    const isMillion = value >= 1_000_000;

    let formattedNumber = value;
    let suffix = '';

    if (isBillion) {
        formattedNumber = value / 1_000_000_000;
        suffix = 'B';
    } else if (isMillion) {
        formattedNumber = value / 1_000_000;
        suffix = 'M';
    }

    const currency = market === 'US' ? 'USD' : 'EUR';

    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return `${formatter.format(formattedNumber)}${suffix}`;
};

export const formatReportingTime = (
    time: string | null,
): { label: string; color: string } => {
    if (time === 'bmo')
        return {
            label: 'Before Open',
            color:
                'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
        };
    if (time === 'amc')
        return {
            label: 'After Close',
            color:
                'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
        };
    return {
        label: 'TBA',
        color:
            'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    };
};

export const formatDateHeader = (dateString: string): string => {
    // Appending T00:00:00 forces LOCAL-time parsing so an ISO date never
    // shifts back a day in negative-UTC-offset timezones.
    const date = new Date(`${dateString}T00:00:00`);
    return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
    }).format(date);
};
